// /account — Plan & billing, Devices, Data & privacy.
// Subscription state comes from the signed license claims (never
// localStorage); devices are read with the user's own JWT through the
// RLS select policy. Revoke/delete go through the license server.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { trialDaysLeft } from '@imagelayerdecomposer/shared';
import { useAuth } from '../auth/AuthProvider';
import { useLicense } from '../license/LicenseProvider';
import { TopBar } from '../ui/TopBar';
import { i18n } from '../ui/i18n';
import { useToast } from '../ui/Toast';
import { getSupabase, isAuthConfigured } from '../lib/supabaseClient';

type DeviceRow = {
  device_id: string;
  label: string | null;
  first_seen_at: string;
  last_seen_at: string;
  revoked_at: string | null;
};

function serverUrl(): string | undefined {
  return import.meta.env['VITE_LICENSE_SERVER_URL'] as string | undefined;
}

export function AccountPage() {
  const { user, logout, error: authError } = useAuth();
  const { claims, refresh } = useLicense();
  const { toast } = useToast();
  const [devices, setDevices] = useState<DeviceRow[] | null>(null);
  const [deviceErr, setDeviceErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    let mounted = true;
    (async () => {
      const sb = getSupabase();
      if (!sb) return; // dev fallback — no device store
      const { data, error: e } = await sb.from('license_devices').select('*');
      if (!mounted) return;
      if (e) setDeviceErr(e.message);
      else setDevices((data ?? []) as DeviceRow[]);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const status = claims?.status ?? null;
  const daysLeft = claims
    ? Math.max(0, Math.floor((claims.exp * 1000 - Date.now()) / 86_400_000))
    : 0;

  const revoke = async (deviceId: string) => {
    setBusy(true);
    try {
      const url = serverUrl();
      if (!url) throw new Error('license server not configured');
      const sb = getSupabase();
      const access = (await sb!.auth.getSession()).data.session?.access_token;
      const res = await fetch(`${url.replace(/\/$/, '')}/license/revoke-device`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${access}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: deviceId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDevices((d) => d?.map((r) => (r.device_id === deviceId ? { ...r, revoked_at: new Date().toISOString() } : r)) ?? null);
      toast(i18n.t('toast.deviceRevoked'));
      await refresh();
    } catch (err) {
      setDeviceErr(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteAccount = async () => {
    if (!confirm('This cancels your subscription and deletes your personal data. Continue?')) return;
    setBusy(true);
    try {
      const url = serverUrl();
      if (url) {
        const sb = getSupabase();
        const access = (await sb!.auth.getSession()).data.session?.access_token;
        await fetch(`${url.replace(/\/$/, '')}/account`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${access}` },
        });
      }
      await logout();
      nav('/login');
    } catch (err) {
      setDeviceErr(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const downloadData = () => {
    exportAccountData().then((data) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'my-account-data.json';
      a.click();
      URL.revokeObjectURL(a.href);
    });
  };

  async function exportAccountData() {
    const sb = getSupabase();
    if (!sb) return { profile: user, subscription: null, devices: null, dev: true };
    const [profile, sub, dev] = await Promise.all([
      sb.from('profiles').select('*').maybeSingle(),
      sb.from('subscriptions').select('*').maybeSingle(),
      sb.from('license_devices').select('*'),
    ]);
    return {
      profile: profile.data,
      subscription: sub.data,
      devices: dev.data,
      exportedAt: new Date().toISOString(),
    };
  }

  return (
    <div data-screen>
      <TopBar section={i18n.t('nav.account')} />
      <main className="auth-main">
        <header className="account-head">
          <span className="avatar avatar-lg">{user?.username?.[0]?.toUpperCase() ?? '?'}</span>
          <div>
            <h1>{user?.username}</h1>
            <div className="status-row">
              <span className={`badge ${status ?? 'expired'}`} data-status={status ?? 'none'}>
                {status === 'trialing'
                  ? `Trial · ${trialDaysLeft(claims ? new Date(claims.exp * 1000).toISOString() : null, Date.now())}d left`
                  : (status ?? 'not licensed')}
              </span>
              {status === 'active' && claims && (
                <span className="dim">renews after {new Date(claims.exp * 1000).toLocaleDateString()}</span>
              )}
            </div>
          </div>
          <div className="account-actions">
            <Link className="btn" to="/app">{i18n.t('nav.backToEditor')}</Link>
            <button className="btn primary" disabled={busy || !serverUrl()} title="Portal session via the payment provider">
              Manage billing
            </button>
          </div>
        </header>

        {isAuthConfigured() && (
          <section className="card account-section" aria-label="Plan and billing">
            <h2>Plan &amp; billing</h2>
            <div className="trial-progress" aria-label="days remaining">
              <div className="bar">
                <div
                  className="fill"
                  style={{
                    width: `${Math.max(4, Math.min(100, ((30 - daysLeft) / 30) * 100))}%`,
                  }}
                />
              </div>
              <span className="dim">{daysLeft > 0 ? `${daysLeft} day(s) remaining` : 'expired'}</span>
            </div>
          </section>
        )}

        <section className="card account-section" aria-label="Devices">
          <h2>Devices</h2>
          {deviceErr && <p className="err-msg" role="alert">({deviceErr})</p>}
          {!devices && !isAuthConfigured() && <p className="dim">Dev mode — device list is unavailable.</p>}
          {devices && devices.length === 0 && <p className="dim">No devices yet — open the editor to register this one.</p>}
          {devices && devices.length > 0 && (
            <table className="device-table">
              <thead>
                <tr><th>Device</th><th>First seen</th><th>Last seen</th><th /></tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.device_id} className={d.revoked_at ? 'revoked' : ''}>
                    <td>
                      {d.device_id.slice(0, 10)}…
                      <span className="dim"> {d.label}</span>
                    </td>
                    <td>{new Date(d.first_seen_at).toLocaleDateString()}</td>
                    <td>{new Date(d.last_seen_at).toLocaleString()}</td>
                    <td>
                      {!d.revoked_at && (
                        <button className="btn danger-btn" disabled={busy} onClick={() => void revoke(d.device_id)}>
                          Revoke
                        </button>
                      )}
                      {d.revoked_at && <span className="dim">revoked</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="card account-section" aria-label="Data and privacy">
          <h2>Data &amp; privacy</h2>
          <p className="dim">
            Design files never reach the server — all image processing happens in your browser.
          </p>
          <div className="row-gap">
            <button className="btn" onClick={downloadData}>
              Download my account data
            </button>
            <button className="btn danger-btn" disabled={busy} onClick={() => void deleteAccount()}>
              Delete account
            </button>
          </div>
          {authError && <p className="err-msg" role="alert">{authError}</p>}
        </section>
      </main>
    </div>
  );
}
